import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import i18nContext from 'components/i18n-context';
import { getErrorMessageForAlert } from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import AuthPageLink from 'components/common/AuthPageLink';
import { CONFIRMATION_HANDLE_TYPES } from 'components/common/constants';
import emailRegistrationStore from 'stores/emailRegistrationStore';
import ComplexTranslate from 'components/ComplexTranslate';
import { CountDown } from 'components/common/CountDown/CountDown';
import Alert from 'uikit/Alert/Alert';
import Button from 'uikit/Button/Button';
import { SecurityInput } from 'uikit/SecurityInput/SecurityInput';
import Loader from 'components/common/Loader';

const logoPath = process.env.REACT_APP_LOGO_PATH;

const RESEND_TIMEOUT = 60;
// Figma's mockup showed 6 pin boxes, but the TRN-6966 API doc confirms email registration codes are always
// 5 digits (phone codes are 6 — this flow never uses those) — confirmed the hard way, by this actually
// hitting the real backend with 6 boxes and being unable to ever fill/submit a genuine 5-digit code. 5 is the
// real, authoritative length.
const CODE_LENGTH = 5;

// TRN-6922 Stage 3 — real design + logic, see docs/specs/TRN-6922_email_registration/spec.md §8.3. The code input
// itself reuses uikit/SecurityInput (type 'email' gives the correct 5-digit numeric layout for an
// email-delivered code — same type PasswordRecovery/EmailConfirmation.js already uses for its own analogous
// code, not 'sms'/'pin' which are for different delivery channels/lengths), rather than the more
// heavily-coupled ConfirmCodeWrapper. Resend-cooldown pattern mirrors PasswordRecovery/EmailConfirmation.js.
const ConfirmStep = () => {
  const i18n = useContext(i18nContext);
  const [code, setCode] = useState('');
  const [showCountDown, setShowCountDown] = useState(true);
  const [sentMessage, setSentMessage] = useState('');

  const { error: storeError, isLoading, email } = emailRegistrationStore;
  const isContinueDisabled = isLoading || code.length !== CODE_LENGTH;

  // TRN-7452: SecurityInput wipes its cells on every error (the boolean `error` below) without reporting it
  // through onComplete, so `code` kept the rejected value and Continue stayed enabled over empty cells —
  // each click re-sent the same wrong code and burned a try. Mirror the wipe so Continue re-enables only
  // once a new code is typed.
  useEffect(() => {
    if (storeError) setCode('');
  }, [storeError]);

  const handleContinue = async () => {
    if (isContinueDisabled) return;
    setSentMessage('');
    await emailRegistrationStore.verifyCode(code);
  };

  const handleResendCode = async () => {
    setSentMessage('');
    await emailRegistrationStore.regenerate();
    // Only restart the 60s cooldown / claim success if regenerate() actually succeeded — a
    // cooldown/limit-exceeded failure sets storeError instead (shown with priority in the Alert below), and
    // restarting the countdown on top of that would falsely imply another resend will work once it ends.
    if (!emailRegistrationStore.error) {
      setShowCountDown(true);
      setSentMessage(i18n.getMessage('pin.alert.sent'));
    }
  };

  return (
    <section className={'registration-form confirm-step'}>
      {/* TRN-7468: same mobile-only logo as EmailStep — the brand panel with its own logo is hidden on mobile. */}
      <a
        href={process.env.REACT_APP_LINK_HOME_PAGE}
        target={'_blank'}
        rel={'noreferrer'}
        className={'registration-form__mobile-logo'}
      >
        <img src={logoPath} alt={'Transferra'} />
      </a>

      <div className={'registration-form__body'}>
        <div className={'registration-form__heading'}>
          <h1 className={'registration-form__title'}>{i18n.getMessage('registration.confirm.title')}</h1>
          <p className={'registration-form__step-subtitle'}>
            <ComplexTranslate
              text={'registration.confirm.subtitle'}
              interpolation={{
                email: <span className={'registration-form__code-email'}>{email}</span>
              }}
            />
          </p>
          <p className={'registration-form__hint'}>
            <button
              type={'button'}
              className={'registration-form__link'}
              onClick={() => emailRegistrationStore.changeEmail()}
              disabled={isLoading}
            >
              {i18n.getMessage('registration.confirm.changeEmail')}
            </button>
          </p>
        </div>

        <Alert
          type={storeError ? 'warning' : 'success'}
          message={(storeError && getErrorMessageForAlert(i18n, storeError)) || sentMessage}
        />

        <div className={'registration-form__code-group'}>
          <div className={'registration-form__code'}>
            <SecurityInput
              type={'email'}
              disabled={isLoading}
              error={!!storeError}
              onComplete={setCode}
              // Default (ON_COMPLETE) mode only calls onComplete once all 5 cells are filled, so editing a
              // completed code (e.g. backspacing a digit) never updates `code` again — length stays at 5 and
              // Continue stays enabled, submitting the stale value. ON_CHANGE reports every keystroke instead,
              // same pattern PopUpGoogle2FAScheme.js already uses for exactly this reason.
              confirmationHandleType={CONFIRMATION_HANDLE_TYPES.ON_CHANGE}
            />
          </div>

          <div className={'registration-form__code-actions'}>
            <Button
              type={'primary'}
              size={'large'}
              fullWidth={true}
              onClick={handleContinue}
              isDisabled={isContinueDisabled}
            >
              {isLoading ? <Loader /> : i18n.getMessage('registration.email.button')}
            </Button>

            <p className={'registration-form__hint'}>
              {i18n.getMessage('registration.confirm.resendPrompt')}{' '}
              {showCountDown ? (
                <>
                  (<CountDown onEnd={() => setShowCountDown(false)} duration={RESEND_TIMEOUT} />)
                </>
              ) : (
                <button type={'button'} className={'registration-form__link'} onClick={handleResendCode}>
                  {i18n.getMessage('sms.resendCode')}
                </button>
              )}
            </p>

            <p className={'registration-form__hint'}>
              {i18n.getMessage('pin.returnToLogIn')}
              <AuthPageLink to={ROUTE_PATHS.LOGIN} className={'registration-form__link'} isDisabled={isLoading}>
                {i18n.getMessage('login.returnToLogIn')}
              </AuthPageLink>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default observer(ConfirmStep);
